import { forwardRef, type ReactNode, type SVGProps } from 'react'
import type { MinimalSize } from '@ovasabi/ui-minimal'

/**
 * Shared icon frame for the Chow glyph set.
 *
 * Every glyph is drawn on a 24×24 grid with a single 1.5px stroke, round
 * caps/joins, `stroke="currentColor"` and `fill="none"` — so an icon inherits
 * the text/tone color of whatever primitive slot it sits in (button, badge,
 * nav item) without any per-icon color wiring.
 *
 * Decorative by default (`aria-hidden`); pass `title` to make it a labelled
 * `img` for standalone, meaning-bearing icons.
 */
const sizeMap: Record<MinimalSize, number> = { sm: 16, md: 20, lg: 24 }

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: MinimalSize | number
  title?: string
  children?: ReactNode
}

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { size = 'md', title, children, ...props },
  ref,
) {
  const px = typeof size === 'number' ? size : sizeMap[size]
  return (
    <svg
      ref={ref}
      width={px}
      height={px}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  )
})
